/**
 * <gff-card-slider> — Horizontal scroll-snap slider with prev/next arrow
 * controls (desktop only) and a draggable progress scrubber (every
 * breakpoint, including mobile, where it replaces the arrows entirely).
 *
 * Markup contract (Liquid emits this):
 *   <gff-card-slider class="gff-bestsellers__slider">
 *     <div data-gff-controls>
 *       <button data-dir="prev" ...></button>
 *       <button data-dir="next" ...></button>
 *     </div>
 *     <ul data-gff-track role="list" tabindex="0">
 *       <li class="gff-bestsellers__cell">…card…</li>
 *       …
 *     </ul>
 *     <div data-gff-progress>
 *       <div data-gff-progress-thumb></div>
 *     </div>
 *   </gff-card-slider>
 *
 * Arrows wrap at the ends (no disabled state) — clicking "next" past the
 * last card returns to the start, and vice versa.
 *
 * The progress bar mirrors the track's real scroll position and can be
 * clicked or dragged to scrub directly. Both the arrows and the progress
 * bar hide themselves automatically when the track has nothing to scroll.
 *
 * No-JS fallback: the track scrolls natively via overflow-x:auto + scroll-snap.
 */
class GffCardSlider extends HTMLElement {
  #track = null;
  #controls = null;
  #prevBtn = null;
  #nextBtn = null;
  #progress = null;
  #progressThumb = null;
  #resizeObserver = null;
  #rafId = null;
  #dragged = false;

  connectedCallback() {
    this.#track         = this.querySelector('[data-gff-track]');
    this.#controls      = this.querySelector('[data-gff-controls]');
    this.#prevBtn        = this.querySelector('[data-dir="prev"]');
    this.#nextBtn        = this.querySelector('[data-dir="next"]');
    this.#progress       = this.querySelector('[data-gff-progress]');
    this.#progressThumb  = this.querySelector('[data-gff-progress-thumb]');

    if (!this.#track) return;

    this.#prevBtn?.addEventListener('click', () => this.#scroll('prev'));
    this.#nextBtn?.addEventListener('click', () => this.#scroll('next'));

    this.#track.addEventListener('scroll', () => this.#queueUpdate(), { passive: true });
    this.#bindProgressDrag();

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.#queueUpdate());
      this.#resizeObserver.observe(this.#track);
    } else {
      window.addEventListener('resize', () => this.#queueUpdate());
    }

    this.#queueUpdate();
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
  }

  /** Returns the width of one card cell + gap (28 px). */
  #stepWidth() {
    const cell = this.#track.querySelector('.gff-bestsellers__cell');
    if (!cell) return 300;
    return cell.getBoundingClientRect().width + 28;
  }

  #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** Animates the track to the nearest card boundary — used after a drag
   * release, since suspending scroll-snap-type during the drag means the
   * browser never auto-corrects the resulting mid-card position on its own. */
  #settleToNearestCard() {
    const step = this.#stepWidth();
    if (!step) return;
    const maxLeft = this.#track.scrollWidth - this.#track.clientWidth;
    const target = Math.min(Math.max(Math.round(this.#track.scrollLeft / step) * step, 0), maxLeft);
    this.#track.scrollTo({ left: target, behavior: this.#reducedMotion() ? 'auto' : 'smooth' });
  }

  /**
   * Scroll the track one step in the given direction, wrapping around at the
   * ends: "next" at the end jumps to the start, "prev" at the start jumps to
   * the end.
   */
  #scroll(dir) {
    const behavior = this.#reducedMotion() ? 'auto' : 'smooth';
    const { scrollLeft, scrollWidth, clientWidth } = this.#track;
    const maxLeft = scrollWidth - clientWidth;

    if (dir === 'next' && scrollLeft >= maxLeft - 1) {
      this.#track.scrollTo({ left: 0, behavior });
    } else if (dir === 'prev' && scrollLeft <= 1) {
      this.#track.scrollTo({ left: maxLeft, behavior });
    } else {
      const delta = this.#stepWidth() * (dir === 'next' ? 1 : -1);
      this.#track.scrollBy({ left: delta, behavior });
    }
  }

  /** Batches scroll/resize updates onto the next animation frame. */
  #queueUpdate() {
    if (this.#rafId) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#updateOverflowState();
      this.#updateThumb();
    });
  }

  /** Hides arrows + progress bar when there's nothing to scroll. */
  #updateOverflowState() {
    const hasOverflow = this.#track.scrollWidth > this.#track.clientWidth + 1;
    this.#controls?.toggleAttribute('hidden', !hasOverflow);
    this.#progress?.toggleAttribute('hidden', !hasOverflow);
  }

  /** Syncs the progress thumb's width + position to the track's scroll state. */
  #updateThumb() {
    if (!this.#progressThumb) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.#track;
    if (scrollWidth <= 0) return;

    const widthPct = Math.max((clientWidth / scrollWidth) * 100, 12);
    const range = scrollWidth - clientWidth;
    const maxLeftPct = 100 - widthPct;
    const posPct = range > 0 ? (scrollLeft / range) * maxLeftPct : 0;

    this.#progressThumb.style.width = `${widthPct}%`;
    this.#progressThumb.style.left = `${posPct}%`;
  }

  /**
   * The thumb's left edge can only travel (bar width − thumb width) px —
   * not the full bar width, since the thumb itself occupies space. Every
   * drag/click calculation must divide by that travel distance, not the
   * raw bar width, or the cursor and thumb desync (the bigger the thumb,
   * the worse the drift — with ~9 cards and ~4 visible the thumb covers
   * roughly half the bar, so this was badly broken before the fix).
   */
  #travelPx() {
    const barWidth = this.#progress.getBoundingClientRect().width;
    const thumbWidth = this.#progressThumb?.getBoundingClientRect().width ?? 0;
    return Math.max(barWidth - thumbWidth, 1);
  }

  /** Lets the user click or drag the progress bar to scrub the track. */
  #bindProgressDrag() {
    if (!this.#progress) return;

    let dragging = false;
    let startX = 0;
    let startScroll = 0;

    const scrollRange = () => this.#track.scrollWidth - this.#track.clientWidth;
    const clamp01 = (n) => Math.min(Math.max(n, 0), 1);

    const onPointerMove = (event) => {
      if (!dragging) return;
      const range = scrollRange();
      if (range <= 0) return;
      const dx = event.clientX - startX;
      if (Math.abs(dx) > 2) this.#dragged = true;
      this.#track.scrollLeft = startScroll + (dx / this.#travelPx()) * range;
    };

    const endDrag = () => {
      dragging = false;
      this.#track.style.scrollBehavior = '';
      this.#track.style.scrollSnapType = '';
      // Restoring scroll-snap-type doesn't retroactively correct an
      // already-settled mid-card position — it only affects future scroll
      // gestures. Explicitly animate to the nearest card boundary so a
      // drag release always ends on a clean, fully-visible card.
      this.#settleToNearestCard();
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', endDrag);
    };

    this.#progress.addEventListener('pointerdown', (event) => {
      dragging = true;
      this.#dragged = false;
      startX = event.clientX;
      startScroll = this.#track.scrollLeft;
      // The track's CSS sets scroll-behavior: smooth and scroll-snap-type:
      // x mandatory. Both also govern plain `scrollLeft = x` assignments,
      // not just scrollTo() — and mandatory snap is enforced on EVERY
      // intermediate write, not only once a scroll gesture settles. Left
      // on, a live drag visibly jumps card-to-card under the cursor
      // instead of tracking it continuously. Suspend both for the live
      // drag only.
      this.#track.style.scrollBehavior = 'auto';
      this.#track.style.scrollSnapType = 'none';
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
    });

    this.#progress.addEventListener('click', (event) => {
      if (this.#dragged) {
        this.#dragged = false;
        return;
      }
      const range = scrollRange();
      if (range <= 0) return;
      const rect = this.#progress.getBoundingClientRect();
      const thumbWidth = this.#progressThumb?.getBoundingClientRect().width ?? 0;
      const ratio = clamp01((event.clientX - rect.left - thumbWidth / 2) / this.#travelPx());
      this.#track.scrollTo({
        left: ratio * range,
        behavior: this.#reducedMotion() ? 'auto' : 'smooth',
      });
    });
  }
}

if (!customElements.get('gff-card-slider')) {
  customElements.define('gff-card-slider', GffCardSlider);
}
